// @flow strict

import React from "react";
import { imgSrc } from "~common/utils";
import { playersMap } from "~robin-hood/utils";
import type { Player } from "~robin-hood/utils";
import type {
  RegisteredStore,
  Equipo,
  LocalColores,
  Colores,
  ColoresRow,
  Color,
} from "~robin-hood/redux/store";
import {
  equipoWithPrueba,
  localStoreWithPrueba,
} from "~robin-hood/redux/store";
import classnames from "~common/classnames";
import styles from "./PruebaColores.module.css";
import window from "~robin-hood/animations/window.module.css";

const COLORES_SECRET: Colores = ["462", "353", "164"];

const COLORES_IMG_PLAYER = playersMap([
  imgSrc("/images/robin-hood/prueba-colores/TRESENRAYA1.png"),
  imgSrc("/images/robin-hood/prueba-colores/TRESENRAYA2.png"),
  imgSrc("/images/robin-hood/prueba-colores/TRESENRAYA3.png"),
  imgSrc("/images/robin-hood/prueba-colores/TRESENRAYA4.png"),
  imgSrc("/images/robin-hood/prueba-colores/TRESENRAYA5.png"),
  imgSrc("/images/robin-hood/prueba-colores/TRESENRAYA6.png"),
]);

const COLOR_PREGUNTA: string = styles.colorPregunta;
const COLOR_VERDE: string = styles.colorVerde;
const COLOR_ROJO: string = styles.colorRojo;
const COLOR_VACIO: string = styles.colorVacio;
const COLORES_STATE: Array<string> = [
  COLOR_VACIO,
  styles.colorAmarillo, // 1
  styles.colorNaranja, // 2
  COLOR_ROJO, // 3
  styles.colorMorado, // 4
  styles.colorAzul, // 5
  COLOR_VERDE, // 6
];

type Props = {
  player: Player,
  equipo: Equipo,
  updateEquipo: (Equipo) => void,
  store: RegisteredStore,
  updateLocalStore: (store: RegisteredStore) => void,
};

export class PruebaColores extends React.PureComponent<Props> {
  render(): React$Node {
    const {
      player,
      store,
      updateLocalStore,
      equipo,
      updateEquipo,
    } = this.props;
    return (
      <div
        className={classnames(styles.pruebaColoresWrapper, window.scaleInBl)}
      >
        <div className={classnames(styles.pruebaColores)}>
          <div
            className={styles.imgWrapper}
            style={{
              backgroundImage: `url(${COLORES_IMG_PLAYER.get(player) || ""})`,
            }}
          ></div>
        </div>
        <Solucion
          player={player}
          equipo={equipo}
          updateEquipo={updateEquipo}
          store={store}
          updateLocalStore={updateLocalStore}
        />
      </div>
    );
  }
}

const DEFAULT_COLORES: Colores = ["000", "000", "000"];

function valueFromLocal(store: RegisteredStore, player: Player): Colores {
  const colores = store.local?.colores;
  return (
    (colores && colores[player] && colores[player].value) || DEFAULT_COLORES
  );
}

function valueFromEquipo(equipo: Equipo): Colores {
  return equipo.pruebas?.colores?.value || DEFAULT_COLORES;
}
function correctFromEquipo(equipo: Equipo): boolean {
  return equipo.pruebas?.colores?.correct || false;
}

function updateLocalWithColores(
  store: RegisteredStore,
  player: Player,
  value: LocalColores
): RegisteredStore {
  return localStoreWithPrueba(store, "colores", {
    [(player: string)]: value,
  });
}

type SolucionProps = {
  player: Player,
  store: RegisteredStore,
  updateLocalStore: (RegisteredStore) => void,
  equipo: Equipo,
  updateEquipo: (Equipo) => void,
};

export class Solucion extends React.PureComponent<SolucionProps> {
  _doCheck = () => {
    const { store, equipo, updateEquipo, player } = this.props;
    const colores = valueFromLocal(store, player);
    const isCorrect = correctFromEquipo(equipo);
    const correct = everyColor(
      colores,
      (c, ri, ci) => COLORES_SECRET[ri][ci] === c
    );
    if (correct && correct !== isCorrect) {
      const updated = equipoWithPrueba(equipo, {
        colores: { value: colores, correct },
      });
      updateEquipo(updated);
    }
  };

  // On change we won't connect with server
  _onColorChange = (row: 0 | 1 | 2, col: 0 | 1 | 2): ((Color) => void) => {
    return (value: Color) => {
      const { store, updateLocalStore, player } = this.props;
      const values = valueFromLocal(store, player);
      if (values[row][col] === value) {
        return;
      }
      const newValues = [...values];
      newValues[row] = replaceAt(values[row], col, value);
      updateLocalStore(
        updateLocalWithColores(store, player, { value: newValues })
      );
    };
  };

  render(): React$Node {
    const { equipo, store, player } = this.props;
    const correct = correctFromEquipo(equipo);
    const values = correct
      ? valueFromEquipo(equipo)
      : valueFromLocal(store, player);
    return (
      <div
        className={classnames(
          styles.solucionWrapper,
          correct && styles.solucionCorrect
        )}
      >
        <div className={styles.solucion}>
          <div className={styles.pruebaControls}>
            <span className={styles.red}></span>
            <span className={styles.yellow}></span>
            <span className={styles.green}></span>
            <span className={classnames(styles.plus, "fa fa-plus")}></span>
          </div>
          {values.map((row, ri) => {
            return (
              <div key={`${ri}`} className={styles.solucionColorRow}>
                {row.split("").map((col, ci) => {
                  return (
                    <SolucionColor
                      key={`${ri}:${ci}`}
                      correct={correct}
                      value={parseInt(col, COLORES_STATE.length)}
                      // $FlowFixMe[incompatible-call]
                      onChange={this._onColorChange(ri, ci)}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
        <SolucionCheck correct={correct} onCheck={this._doCheck} />
      </div>
    );
  }
}

type CheckProps = {
  correct: boolean,
  onCheck: () => void,
};

const SPINNER = { interval: 100, frames: COLORES_STATE };
const RESET = 2000;

function next(color: ?Color): Color {
  return ((color ?? 0) + 1) % COLORES_STATE.length;
}

class SolucionCheck extends React.PureComponent<
  CheckProps,
  { checking?: Color, checked: boolean, tick: number }
> {
  _mounted: boolean;
  _timeout: ?TimeoutID;
  state = { checking: undefined, checked: !!this.props.correct, tick: 0 };

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = undefined;
    }
  }

  _tick = () => {
    if (!this._mounted) {
      clearTimeout(this._timeout);
      this._timeout = undefined;
      return;
    }
    this.setState(
      (state) => ({
        tick: state.tick + 1,
        checking: next(state.checking),
      }),
      () => {
        if (!this._mounted || this._timeout) return;
        const TICKS = SPINNER.frames.length * 3;
        if (this.state.tick === TICKS - 1) {
          // Check early so result is ready
          this.props.onCheck();
        }
        if (this.state.tick < TICKS) {
          this._timeout = setTimeout(() => {
            this._timeout = undefined;
            this._tick();
          }, SPINNER.interval);
        } else {
          // Finished
          this.setState(
            {
              tick: 0,
              checking: undefined,
              checked: true,
            },
            () => {
              this._timeout = setTimeout(() => {
                this.setState({ checked: false });
                this._timeout = undefined;
              }, RESET);
            }
          );
        }
      }
    );
  };

  _onClick = () => {
    const { correct } = this.props;
    const { checking, checked } = this.state;
    if (correct || checking || this._timeout) return;
    if (checked) {
      this.setState({ checked: false });
    } else {
      this._tick();
    }
  };

  render() {
    const { correct } = this.props;
    const { checking, checked } = this.state;
    const className: string = correct
      ? COLOR_VERDE
      : checking != null
      ? SPINNER.frames[checking]
      : !checked
      ? COLOR_PREGUNTA
      : COLOR_ROJO;

    const content =
      className === COLOR_PREGUNTA ? (
        <span className="fa fa-question"></span>
      ) : null;
    return (
      <div className={classnames(styles.solucionCheckWrapper)}>
        <div
          className={classnames(
            styles.solucionColor,
            styles.solucionCheck,
            className
          )}
          onClick={this._onClick}
        >
          {content}
        </div>
      </div>
    );
  }
}

type ColorProps = {
  correct: boolean,
  value: number,
  onChange: (Color) => void,
};

function SolucionColor(props: ColorProps) {
  const className = COLORES_STATE[props.value];
  return (
    <div
      className={classnames(className, styles.solucionColor)}
      onClick={() => {
        props.onChange &&
          props.onChange((props.value + 1) % COLORES_STATE.length);
      }}
    ></div>
  );
}

function everyColor(
  colores: Colores,
  callback: (Color, 0 | 1 | 2, 0 | 1 | 2) => boolean
): boolean {
  return colores.every((row, ri) =>
    // $FlowFixMe[incompatible-call]
    row.split("").every((c, ci) => callback(c, ri, ci))
  );
}

function replaceAt(
  colores: ColoresRow,
  idx: 0 | 1 | 2,
  color: Color
): ColoresRow {
  return idx === 0
    ? color + colores[1] + colores[2]
    : idx === 1
    ? colores[0] + color + colores[2]
    : colores[0] + colores[1] + color;
}
